import { Button, Stack, Typography } from '@mui/material'
import { forwardRef, useEffect, useMemo } from 'react'
import { FieldError, UseFormSetValue } from 'react-hook-form'
import {
  DatePickerField,
  MaskedInputField,
  MaskedInputFieldProps,
  RadioButtonsGroup,
  TextInputField,
  TextInputFieldProps,
} from 'utoniq-ui'

import {
  Gender,
  Maybe,
  UserDeliveryDetailFragment,
} from '@/clients/utoniq-core/schema'
import {
  tLabel,
  tMessage,
  useForm,
  UseFormProps,
  useMessage,
  useTranslation,
} from '@/core'
import { Cell, CellFrame, Organism, OrganismFrame } from '@/core/architecture'
import { individualUseFullNameKatakana } from '@/core/individual'
import { validateFullName, validatePhoneNumber } from '@/core/validators'
import { dayjs } from '@/lib/dayjs'

type LotteryUserFormProps = {
  user: Maybe<UserDeliveryDetailFragment>
  onSubmit: (value: LotteryUserValue) => Promise<void>
}

export type LotteryUserValue = {
  fullName: string
  phoneNumber: string
  birthday: string
  gender: string
}

const schema: UseFormProps['schema'] = o => ({
  fullName: validateFullName(
    o,
    individualUseFullNameKatakana()
      ? 'lotteryUserInfo.fullNameKatakana'
      : 'lotteryUserInfo.fullName'
  ),
  birthday: o.date().label(tLabel('lotteryUserInfo.birthday')),
  gender: o
    .string()
    .label(tLabel('lotteryUserInfo.gender'))
    .messages({
      'string.empty': tMessage('lotteryUserInfo.gender.string.empty'),
    }),
  phoneNumber: validatePhoneNumber(o, 'lotteryUserInfo.phoneNumber'),
})

export const LotteryUserForm: Organism<LotteryUserFormProps> = ({
  user,
  onSubmit,
}) => {
  const { t } = useTranslation('lotteryUserInfo')
  const { showError } = useMessage()

  const {
    register,
    watch,
    setValue,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<LotteryUserValue>({ schema, defaultValues })

  useEffect(() => {
    if (user) {
      reset({
        fullName: user.fullName ?? '',
        gender: user.gender ?? '',
        birthday: user.birthday ?? '',
        phoneNumber: user.phoneNumber ?? '',
      })
    }
  }, [user])

  const handleOnSubmit = handleSubmit(async values => {
    await onSubmit(values).catch(showError)
  })

  return (
    <OrganismFrame sx={{ py: 4 }}>
      <Typography variant="subtitle1" sx={{ pb: 4 }}>
        {t('preSaleUserNote', 'お客様の情報をご入力ください。')}
      </Typography>
      <TextInputCell
        type="text"
        error={errors?.fullName}
        {...register('fullName')}
        titleProps={{
          children: t(
            individualUseFullNameKatakana()
              ? 'fullNameTitleKatakana'
              : 'fullNameTitle'
          ),
        }}
        textFieldProps={{
          placeholder: t(
            individualUseFullNameKatakana()
              ? 'fullNamePlaceholderKatakana'
              : 'fullNamePlaceholder'
          ),
        }}
      />
      <RadioInputCell
        value={watch('gender')}
        setValue={setValue}
        error={errors?.gender}
      />
      <DatePickerCell
        error={errors.birthday}
        setValue={setValue}
        value={watch('birthday')}
      />
      <MaskedInputCell
        type="tel"
        mask="00000000000"
        error={errors?.phoneNumber}
        {...register('phoneNumber')}
      />
      <Stack flex={1} alignItems="center" sx={{ pb: 5 }}>
        <Button
          type="submit"
          size="large"
          sx={{ width: 244 }}
          loading={isSubmitting}
          disabled={isSubmitting}
          onClick={handleOnSubmit}
        >
          {t('submit')}
        </Button>
      </Stack>
    </OrganismFrame>
  )
}

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('lotteryUserInfo')
    return (
      <CellFrame>
        <TextInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)

type RadioInputCellProps = {
  value: string
  setValue: UseFormSetValue<LotteryUserValue>
  error?: FieldError
}
const RadioInputCell: Cell<RadioInputCellProps> = ({
  value,
  setValue,
  error,
}) => {
  const { t } = useTranslation('lotteryUserInfo')
  return (
    <CellFrame sx={{ pb: 5 }}>
      <RadioButtonsGroup
        value={value}
        error={error}
        title="gender"
        t={t}
        sx={{ flexDirection: 'row' }}
        onChange={value => {
          setValue('gender', value as Gender, { shouldValidate: true })
        }}
        listItems={[
          { label: t(Gender.Female), value: Gender.Female },
          { label: t(Gender.Male), value: Gender.Male },
          { label: t(Gender.None), value: Gender.None },
        ]}
      />
    </CellFrame>
  )
}

type DatePickerCellProps = {
  setValue: UseFormSetValue<LotteryUserValue>
  value: string | null
  error?: FieldError
}
const DatePickerCell: Cell<DatePickerCellProps> = ({
  setValue,
  error,
  value,
}) => {
  const { t } = useTranslation('lotteryUserInfo')
  const birthdayValue = useMemo(() => (value ? dayjs(value) : null), [value])
  return (
    <CellFrame sx={{ pb: 5 }}>
      <DatePickerField
        error={error}
        onChange={date => {
          setValue('birthday', date as never, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }}
        value={birthdayValue}
        textFieldProps={{
          inputProps: {
            value: value ? dayjs(value).format('YYYY/MM/DD') : '',
          },
        }}
        title={t('birthday')}
        placeholder={t('birthdayPlaceholder')}
        titleProps={{
          variant: 'subtitle1',
          color: 'text.secondary',
        }}
      />
    </CellFrame>
  )
}
const MaskedInputCell = forwardRef(
  (
    { textFieldProps, error, ...props }: Omit<MaskedInputFieldProps, 't'>,
    ref
  ) => {
    const { t } = useTranslation('lotteryUserInfo')
    return (
      <CellFrame>
        <MaskedInputField
          {...props}
          textFieldProps={textFieldProps}
          ref={ref}
          t={t}
          error={error}
          placeholder={t(`${props.name}Placeholder`)}
        />
      </CellFrame>
    )
  }
)
const defaultValues: LotteryUserValue = {
  fullName: '',
  phoneNumber: '',
  birthday: '',
  gender: '',
}
